/**
 * The pieces both hour grids are drawn from — the day card on «Сьогодні» and
 * the week overlay behind its «На весь екран» button.
 *
 * They were one component's private helpers until the week grid needed the
 * same block, the same colour rules and the same gutter clock. Two copies of
 * "how a Google event looks in this app" is how one of them ends up with a
 * different tint a month later, so the markup lives here and the grids only
 * decide where to put it.
 */
import type { CalendarEvent } from "../../types";
import { eventTimeLabel, type DayBlock } from "../../lib/agenda";

/** Short of this a block cannot hold its own title, so it is given the room. */
export const MIN_BLOCK_PX = 20;

/** A block taller than this has room for its own time under the title. */
const TIME_LINE_PX = 36;

/** Beyond this many side-by-side columns a right-hand label is unreadable. */
const LABEL_MAX_COLUMNS = 2;

/** The event's own colour, or the accent when Google gave none. */
export const hue = (color: string | null): string =>
  color ?? "var(--color-accent)";

/** The same colour as a ground: soft enough that token text still reads on it. */
export const tint = (color: string | null): string =>
  `color-mix(in srgb, ${hue(color)} 22%, transparent)`;

/** 570 -> "09:30". A clock time, so midnight is "00:00". */
export const hm = (min: number): string =>
  `${String(Math.floor(min / 60) % 24).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;

/**
 * A line of the grid, which is a boundary rather than a clock reading: the
 * last one closes the day at "24:00". Printed as "00:00" it would read as the
 * midnight the grid *starts* at, sitting under 23:00 and above nothing.
 */
export const gridHour = (min: number): string =>
  min === 24 * 60 ? "24:00" : hm(min);

interface EventBlockProps {
  block: DayBlock;
  /** Minutes at the top of the grid this block is being placed in. */
  from: number;
  /** Pixels per hour of that grid. */
  hourPx: number;
  /**
   * A week column, roughly 7rem wide. The side label moves under the title —
   * a title sharing one 110px line with «Google Meet» is two unreadable
   * halves — and the whole block drops to the smallest readable size.
   */
  narrow?: boolean;
}

/**
 * One event, at true scale.
 *
 * The colour is Google's own — the event's `colorId`, else its calendar — so a
 * block looks here the way it looks in the app it came from. It arrives as a
 * hex, which is the one place this codebase renders a colour it did not define:
 * a soft `color-mix` tint for the ground and the full hue for the 3px bar, with
 * every glyph of text still on the palette's own tokens. The bar is a
 * positioned span rather than a `border-left` so it stays a mark on a block and
 * cannot be mistaken for the accent stripe DESIGN.md §6 forbids on cards.
 */
export default function EventBlock({
  block,
  from,
  hourPx,
  narrow = false,
}: EventBlockProps) {
  const { event, columns, column } = block;
  const top = ((block.start - from) / 60) * hourPx;
  const height = Math.max(
    MIN_BLOCK_PX,
    ((block.end - block.start) / 60) * hourPx,
  );
  const width = 100 / columns;
  // The event's own hours, not the block's: a five-minute stand-up is given
  // fifteen minutes of grid so its title fits, and saying «09:00 – 09:15»
  // under it would be the widget inventing a meeting length.
  const time = eventTimeLabel(event);

  return (
    <div
      className="absolute overflow-hidden rounded-control py-0.5 pr-1.5 pl-2.5"
      style={{
        top,
        height,
        left: `${column * width}%`,
        width: `calc(${width}% - 3px)`,
        background: tint(event.color),
      }}
      title={`${event.title} · ${time}${event.location ? ` · ${event.location}` : ""}`}
    >
      <span
        className="absolute inset-y-0 left-0 w-[3px] rounded-full"
        style={{ background: hue(event.color) }}
        aria-hidden="true"
      />
      {narrow ? (
        <>
          <div className="truncate text-micro text-fg">{event.title}</div>
          {height >= TIME_LINE_PX && (
            <div className="flex items-baseline gap-1 text-micro">
              <span className="jv-num min-w-0 truncate text-muted">{time}</span>
              {event.meet_url && (
                <a
                  href={event.meet_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 text-accent hover:underline"
                  title="Google Meet"
                >
                  Meet
                </a>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="flex items-baseline gap-1.5">
            <span className="min-w-0 flex-1 truncate text-xs text-fg">
              {event.title}
            </span>
            {columns <= LABEL_MAX_COLUMNS && <SideLabel event={event} />}
          </div>
          {height >= TIME_LINE_PX && (
            <div className="jv-num text-micro text-muted">{time}</div>
          )}
        </>
      )}
    </div>
  );
}

/** Where it is, or the tab to open — never both, and never a duplicate of the time. */
function SideLabel({ event }: { event: CalendarEvent }) {
  if (event.meet_url) {
    return (
      <a
        href={event.meet_url}
        target="_blank"
        rel="noopener noreferrer"
        className="shrink-0 text-micro text-accent hover:underline"
      >
        Google Meet
      </a>
    );
  }
  if (event.location) {
    return (
      <span
        className="max-w-[50%] shrink truncate text-micro text-muted"
        title={event.location}
      >
        {event.location}
      </span>
    );
  }
  return null;
}

/**
 * An event with no hour of its own — a holiday, a birthday, a trip. Drawn as a
 * chip above the grid in both views, because it has no place *in* a column of
 * hours and printing it at 00:00 would state a meeting time that never existed.
 */
export function AllDayChip({ event }: { event: CalendarEvent }) {
  return (
    <span
      className="flex min-w-0 items-center gap-1.5 rounded-control px-2 py-1"
      style={{ background: tint(event.color) }}
    >
      <span
        className="size-1.5 shrink-0 rounded-full"
        style={{ background: hue(event.color) }}
        aria-hidden="true"
      />
      <span className="min-w-0 truncate text-micro text-fg" title={event.title}>
        {event.title}
      </span>
    </span>
  );
}
